import type { BeforeSendFn, PostHog, Properties } from 'posthog-js'

// Query strings and fragments in RxPad URLs carry personal data: the
// prescriptions search term (?search=), SSO launch parameters (patientId,
// referralId and whatever else the PMS appends), the OAuth code on /auth.
// PostHog copies URLs into many places ($current_url, $referrer, $initial_*,
// $session_entry_*, hrefs in $elements_chain, $heatmap_data keys, web vitals),
// so rather than chase a list of properties, every URL in an event keeps its
// path and loses everything after it.

const URL_OR_PATH = /^(?:[a-z][a-z\d+.-]*:\/\/|\/)/i
const QUERY_OR_FRAGMENT = /[?#][\s\S]*$/
// href="…" and attr__href="…" inside $elements_chain, where quotes are escaped as \"
const HREF_IN_ELEMENTS_CHAIN = /(href=")((?:[^"\\]|\\.)*)"/g
// Inside longer text a query has no reliable end (values can hold quotes or
// raw spaces), so the query and the rest of the text after it go.
const URL_QUERY_IN_TEXT = /([a-z][a-z\d+.-]*:\/\/[^\s?#]*)[?#][\s\S]*$/i
// Deep enough for every property PostHog builds; anything deeper is left as is.
const MAX_DEPTH = 8

const stripQueryAndFragment = (url: string): string => url.replace(QUERY_OR_FRAGMENT, '')

export const stripUrlQueries = (value: string): string =>
  URL_OR_PATH.test(value)
    ? stripQueryAndFragment(value)
    : value
        .replace(
          HREF_IN_ELEMENTS_CHAIN,
          (_, prefix, href) => `${prefix}${stripQueryAndFragment(href)}"`
        )
        .replace(URL_QUERY_IN_TEXT, '$1')

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === '[object Object]'

const scrub = (value: unknown, depth: number): unknown => {
  if (typeof value === 'string') return stripUrlQueries(value)
  if (depth >= MAX_DEPTH) return value
  if (Array.isArray(value)) return value.map((item) => scrub(item, depth + 1))
  if (!isPlainObject(value)) return value

  const scrubbed: Record<string, unknown> = {}
  Object.entries(value).forEach(([key, item]) => {
    // Keys can be URLs too ($heatmap_data). Two URLs that differ only in their
    // query collapse to one key, so keep both lists of points.
    const scrubbedKey = stripUrlQueries(key)
    const scrubbedItem = scrub(item, depth + 1)
    const existing = scrubbed[scrubbedKey]
    scrubbed[scrubbedKey] =
      Array.isArray(existing) && Array.isArray(scrubbedItem)
        ? [...existing, ...scrubbedItem]
        : scrubbedItem
  })
  return scrubbed
}

const scrubProperties = (properties: Properties): Properties => scrub(properties, 0) as Properties

export const scrubUrlQueries: BeforeSendFn = (event) => {
  if (!event) return event
  return {
    ...event,
    properties: scrubProperties(event.properties),
    ...(event.$set && { $set: scrubProperties(event.$set) }),
    ...(event.$set_once && { $set_once: scrubProperties(event.$set_once) }),
  }
}

// Where PostHog keeps the first URL a browser landed on. It is resent with
// every feature-flag request, which before_send does not see.
const STORED_LANDING_URL_KEYS = [
  '$initial_person_info',
  '$initial_referrer_info',
  '$initial_campaign_params',
]

// Browsers that ran an earlier build still hold the landing URL and the
// clinician's name and email (cached by identify) in PostHog's cookie and
// localStorage, and would keep sending them with every feature-flag request.
export const forgetStoredPersonalData = (ph: PostHog): void => {
  STORED_LANDING_URL_KEYS.forEach((key) => ph.unregister(key))
  ph.resetPersonPropertiesForFlags()
}
